/**
 * @Description 鼠标光标类，处理鼠标交互
 * @Author iCEA
 * @Date 2020-10-29
 * @Version 1.0
 * @todo 1、点击点位编辑点修改mgraph
 */

import * as THREE from 'three'
import { Model } from './Model'
import { Renderer } from './Renderer'
import { CPointSet } from './CameraPoints'
import { EditPointSet } from './EditPoints'
import { HotpointSet } from './Hotpoint'
import { config } from './config'
import { Listener } from './Listener'
import { ScreenInput } from './Input/ScreenInput'
import * as Utils from './Utils'
// import { HotpointSet, TitleHotpoint, MusicHotpoint } from './Hotpoint'

/**
 * 光标事件类
 */
export class CursorEvent {
  /**
   * 构造函数
   * @param {THREE.Vector3} pos 光标所指的世界坐标
   * @param {THREE.Vector3} normal 光标所指的位置的法线
   * @param {THREE.Vector3} camPos 当前光标的摄像机位置
   * @param {number} camIdx 当前光标的摄像机点位
   * @param {number} faceId 面朝id
   */
  constructor (pos, normal, camPos, camIdx, faceId) {
    /**
     * @type {THREE.Vector3} 光标所指的世界坐标
     */
    this.pos = pos
    /**
     * @type {THREE.Vector3} 光标所指的位置的法线
     */
    this.normal = normal
    /**
     * @type {THREE.Vector3} 当前光标的摄像机位置
     */
    this.cameraPos = camPos
    /**
     * @type {number} 当前光标的摄像机点位
     */
    this.cameraIdx = camIdx
    /**
     * @type {number} 面朝id
     */
    this.coverFaceId = faceId
  }
}

class Cursor extends ScreenInput {
  /**
   * 光标构造函数
   * @param {string} cursorUrl 光标图标
   * @param {Element} eventElement 监听事件的元素
   * @param {THREE.Scene} sceneRoot 场景根节点
   * @param {Renderer} renderer 渲染器实例
   * @param {CPointSet} cps 已开启的点位集实例
   * @param {Model} model 模型实例
   * @param {HotpointSet} hps 热点实例
   * @param {boolean} isEdit 是否为后台编辑模式
   * @param {EditPointSet} eps 所有点位编辑模式实例
   */
  constructor (cursorUrl, eventElement, sceneRoot, renderer, cps, model, hps, isEdit, eps) {
    super(cps)
    this.isEdit = isEdit // 是否编辑模式
    /**
     * @type {THREE.Mesh} 光标Mesh
     */
    this.cursorMesh = null
    /**
     * @type {THREE.Vector2} 光标屏幕坐标
     */
    this.mouseCoords = new THREE.Vector2()

    /**
     * @type {number} 屏幕物理分辨率
     */
    this.winRatio = window.devicePixelRatio

    /**
     * @type {Element} 用于监听事件的dom元素
     */
    this.eventElement = eventElement
    /**
     * @type {number} 滚轮事件次数
     */
    this.wheelCount = 0
    /**
     * @type {number} 上一个时间戳
     */
    this.lastTimestamp = 0.0
    /**
     * @type {boolean} 鼠标拖拽事件区分，true为拖拽事件，false为点击事件
     */
    this.dragFlag = false

    /**
     * @type {boolean} 鼠标是否按下
     */
    this.rightDown = false
    /**
     * @type {boolean} 鼠标是否按下
     */
    this.rightUp = false

    /**
     * @type {boolean} 左键松开的标志变量，在相机控制器中监听此变量
     */
    this.leftDown = false
    /**
     * @type {boolean} 左键松开的标志变量，在相机控制器中监听此变量
     */
    this.leftUp = false
    /**
     * @type {boolean} 滚轮事件标志变量，在相机控制器中监听此变量
     */
    this.Wheel = false

    /**
     * @type {THREE.Object3D} 当前指向的热点
     */
    this.currHotpoint = null
    /**
     * @type {THREE.Object3D} 当前指向的编辑点
     */
    this.currEditPoint = null
    /**
     * @type {{hotpoint:symbol,ui:symbol,free:symbol,None:Symbol}} 状态枚举
     */
    this.enumState = Object.freeze({
      hotpoint: Symbol('hotpoit'),
      ui: Symbol('ui'),
      free: Symbol('free'),
      None: Symbol('None')
    })
    /**
     * @type {symbol} 光标状态
     */
    this.state = this.enumState.free

    /**
     * @type {boolean} 是否在Cnavas内
     */
    this.inCanvas = false

    /**
     * @type {boolean} 是否需要更新
     */
    this.needUpdate = true

    /**
     * @type {HotpointSet} 热点列表
     */
    this.hotpointSet = hps

    /**
     * @type {EditPointSet} 编辑点位列表
     */
    this.editpointSet = eps

    /**
     * @type {Listener} 隐藏hud样式（透明度）的函数列表
     */
    this.hideHUDListener = new Listener()
    /**
     * @type {Listener} 浮现hud样式（透明度）的函数列表
     */
    this.showHUDListener = new Listener()

    /**
     * @type {{panoZooming:boolean,panoRotate:boolean,godRotate:boolean,godPanning:boolean,godZooming:boolean}} 操作类型
     */
    this.operation = {
      panoZooming: false,
      panoRotate: false,
      godRotate: false,
      godPanning: false,
      godZooming: false
    }
    /**
     * @type {number} 总位移X
     */
    this.totalX = 0
    /**
     * @type {number} 总位移Y
     */
    this.totalY = 0
    /**
     * @type {number} 总滚轮位移
     */
    this.totalWheelY = 0

    /**
     * @type {[{event:string,handler:function}]} event事件绑定方法数组
     */
    this.eventsBind = [
      { event: 'mousedown', handler: this.onDocumentMouseDown.bind(this) },
      { event: 'mousemove', handler: this.onDocumentMouseMove1.bind(this) },
      { event: 'wheel', handler: this.onDocumentMouseWheel.bind(this) },
      { event: 'mouseenter', handler: this.onInCanvas.bind(this) },
      { event: 'mouseout', handler: this.onOutCanvas.bind(this) }
    ]

    /**
     * @type {{move:function,noEventMove:function,rightUp:function,leftUp:function,midUp:funcion}} 事件类型触发方法
     */
    this.processingEventsBind = {
      move: this.onDocumentMouseMove.bind(this),
      noEventMove: this.onDrag.bind(this),

      rightUp: this.onDocumentMouseUp2.bind(this),
      leftUp: this.onDocumentMouseUp0.bind(this),
      midUp: this.onDocumentMouseUp1.bind(this)

    }

    /**
     * @type {Renderer} 渲染器实例
     */
    this.renderer = renderer
    /**
     * @type {THREE.Scene} 场景根结点
     */
    this.sceneRoot = sceneRoot
    /**
     * @type {Model} 模型实例
     */
    this.model = model
    this.isOffset = true
    this.createIcon(cursorUrl)
  }

  /**
   * 创建光标图标
   * @param {string} cursorUrl 光标图标
   */
  createIcon (cursorUrl) {
    const moveGeo = new THREE.PlaneBufferGeometry(256, 256)
    const texture = new THREE.TextureLoader().load(cursorUrl)
    const moveMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, map: texture, opacity: 0.5, transparent: true })
    moveMaterial.depthTest = false
    this.meshIcon = new THREE.Mesh(moveGeo, moveMaterial)
    this.meshIcon.position.set(1000000, 1000000, 1000000)

    moveGeo.dispose()
    moveMaterial.dispose()
    // 创建icon
    const material = new THREE.SpriteMaterial({ transparent: true, alphaTest: 0.1 })
    this.spriteIcon = new THREE.Sprite(material)
    this.spriteIcon.scale.set(200, 200, 1)
    this.spriteIcon.visible = false

    material.dispose()
    this.cursorMesh = this.meshIcon
  }

  /**
   * 初始化光标
   * @param {string} cursorUrl 光标贴图url
   */
  init (cursorUrl) {
    if (!this.spriteIcon) {
      this.createIcon(cursorUrl)
    }
    this.sceneRoot.add(this.spriteIcon)
    this.sceneRoot.add(this.meshIcon)

    // 鼠标事件类
    for (const item of this.eventsBind) {
      this.eventElement.addEventListener(item.event, item.handler, false)
    }
  }

  /**
   * 释放资源方法
   */
  depose () {
    this.sceneRoot.remove(this.meshIcon)
    this.sceneRoot.remove(this.spriteIcon)

    // 鼠标事件类
    for (const item of this.eventsBind) {
      this.eventElement.removeEventListener(item.event, item.handler, false)
    }
  }

  /**
   * @returns {boolean} 获取光标是否可视
   */
  get visible () {
    return this.cursorMesh.visible
  }

  /**
   * @param {boolean} value 设置光标是否可视
   */
  set visible (value) {
    this.cursorMesh.visible = value
  }

  /**
   * 进入Canvas时触发
   * @param {Event} event 触发事件
   */
  onInCanvas (event) {
    this.renderer.active = true
    this.inCanvas = true
  }

  /**
   * 离开Canvas时触发
   * @param {Event} event 触发事件
   */
  onOutCanvas (event) {
    this.renderer.active = false
    this.inCanvas = false
  }

  /**
   * 鼠标移动触发事件处理函数，通过射线更新光标位置
   * @param {object} event 鼠标移动事件对象
   */
  onDocumentMouseMove1 (event) {
    if (!this.isCouserUsable(event)) return
    event.preventDefault()
    this.updateCoords(event)
  }

  /**
   * 鼠标对应的二维坐标位置
   * @param {event} event 鼠标事件
   */
  updateCoords (event) {
    this.mouseCoords.x = (event.offsetX * this.winRatio / this.eventElement.width) * 2 - 1
    this.mouseCoords.y = -(event.offsetY * this.winRatio / this.eventElement.height) * 2 + 1
  }

  /**
   * 鼠标键按下事件处理函数
   * @param {object} event 鼠标按下事件对象
   */
  onDocumentMouseDown (event) {
    if (!this.isCouserUsable(event)) {
      this.renderer.active = false
      return
    }

    this.renderer.active = true // 是否激活
    if (this.enable) {
      if (this.cam.cameraMode) {
        this.hideHUDListener.invoke()
      }

      event.preventDefault()
      this.down = true // 打断状态

      if (event.button === 0) { // 0左键 1中键 2右键
        // 保证监听拖拽事件
        this.leftDown = true
        this.eventElement.addEventListener('mousemove', this.processingEventsBind.move, false)
        this.eventElement.addEventListener('mouseup', this.processingEventsBind.leftUp, false)
      } else if (event.button === 1 && !this.cam.cameraMode) {
        this.eventElement.addEventListener('mousemove', this.processingEventsBind.move, false)
        this.eventElement.addEventListener('mouseup', this.processingEventsBind.midUp, false)
      } else if (event.button === 2) {
        this.rightDown = true
        if (!this.cam.cameraMode) {
          this.eventElement.addEventListener('mousemove', this.processingEventsBind.move, false)
        } else {
          this.eventElement.addEventListener('mousemove', this.processingEventsBind.noEventMove, false)
        }
        this.eventElement.addEventListener('mouseup', this.processingEventsBind.rightUp, false)
      }
    }
  }

  /**
   * 鼠标拖拽移动事件处理函数
   * @param {object} event 鼠标移动事件对象
   */
  onDrag (event) {
    const mvX = event.movementX || event.mozMovementX || event.webkitMovementX || 0
    const mvY = event.movementY || event.mozMovementY || event.webkitMovementY || 0
    this.dragFlag = this.dragFlag || mvX !== 0 || mvY !== 0
  }

  /**
   * 鼠标拖拽移动事件处理函数
   * @param {object} event 鼠标移动事件对象
   */
  onDocumentMouseMove (event) {
    if (!this.isCouserUsable(event)) return
    if (this.enable) {
      // 拖拽事件
      const mvX = event.movementX || event.mozMovementX || event.webkitMovementX || 0
      const mvY = event.movementY || event.mozMovementY || event.webkitMovementY || 0
      this.dragFlag = this.dragFlag || mvX !== 0 || mvY !== 0
      if (!this.dragFlag) return
      this.totalX += mvX
      this.totalY += mvY

      // 上面变累计值，然后在更新时更新下面几句
      if (this.cam.cameraMode) {
        if (event.which === 1) {
          this.operation.panoRotate = true
        }
      } else { // god mode
        switch (event.which) {
          case 1: // 左键拖拽
            this.operation.godRotate = true
            break
          case 2: // 中键拖拽
            this.operation.godZooming = true
            break
          case 3: // 右键拖拽
            this.operation.godPanning = true
        }
      }
    }
  }

  /**
   * 鼠标左键弹起事件处理函数
   * @param {object} event 鼠标弹起事件对象
   */
  onDocumentMouseUp0 (event) {
    this.leftUp = true
    if (this.cam.cameraMode) {
      this.showHUDListener.invoke()
    }
    this.eventElement.removeEventListener('mousemove', this.processingEventsBind.move)
    this.eventElement.removeEventListener('mouseup', this.processingEventsBind.leftUp)
    if (!this.isCouserUsable(event)) return
    if (this.enable) {
      this.leftUp = true // 左键松开标志
    }
  }

  /**
   * 鼠标中键弹起事件处理函数
   * @param {object} event 鼠标弹起事件对象
   */
  onDocumentMouseUp1 (event) {
    if (this.cam.cameraMode) {
      this.showHUDListener.invoke()
    }
    this.eventElement.removeEventListener('mousemove', this.processingEventsBind.move)
    this.eventElement.removeEventListener('mouseup', this.processingEventsBind.midUp)
    // 鼠标中键事件
  }

  /**
   * 鼠标右键弹起事件处理函数
   * @param {object} event 鼠标弹起事件对象
   */
  onDocumentMouseUp2 (event) {
    if (this.cam.cameraMode) {
      this.showHUDListener.invoke()
    }
    this.eventElement.removeEventListener('mousemove', this.processingEventsBind.move)
    this.eventElement.removeEventListener('mouseup', this.processingEventsBind.rightUp)
    this.eventElement.removeEventListener('mousemove', this.processingEventsBind.noEventMove)

    if (!this.isCouserUsable(event)) return
    if (this.enable) {
      this.rightUp = true // 左键松开标志
    }
  }

  /**
   * 鼠标滚轮滑动事件处理函数，缩放视角
   * @param {object} event 鼠标滚轮事件对象
   */
  onDocumentMouseWheel (event) {
    if (!this.isCouserUsable(event)) return

    this.totalWheelY += event.deltaY

    if (this.enable) {
      if (this.cam.cameraMode) {
        this.operation.panoZooming = true
        this.handleCameraModeWheelEvent(event, this.cam)
      } else {
        this.operation.godZooming = true
      }
    }
  }

  /**
   * 处理全景模式下滚轮事件
   * @param {Event} event 鼠标滚轮事件
   * @param {Camera} cam 相机实例
   */
  handleCameraModeWheelEvent (event, cam) {
    const delta = event.timeStamp - this.lastTimestamp
    this.lastTimestamp = event.timeStamp
    // timestamp差值在600内计数+1
    if (delta < 600 && cam.fov === 65) {
      this.wheelCount++
      if (this.wheelCount > 5) {
        // 计数大于5则进入上帝视角
        this.Wheel = true // 在控制器类里执行change2god
        this.wheelCount = 0
      }
    } else {
      this.wheelCount = 0
    }
  }

  /**
   * 鼠标交互更新，包括热点、状态
   */
  interactUpdate () {
    this.raycaster.setFromCamera(this.mouseCoords, this.cam.camera)

    const interactHp = this.hotpointSet.interact(this.raycaster)
    const interactUi = this.isEdit ? this.editpointSet.interact(this.raycaster) : null

    // 射中对象不同，触发in out
    this.hotpointUpdate(interactHp)
    // 更新currEditPoint
    this.cpointUiUpdate(interactUi)

    this.stateUpdate(interactHp, interactUi)
    this.cursorUpdate()
  }

  /**
   * 相机状态更新方法
   */
  camUpdate () {
    const cam = this.cam
    // cameraMode
    if (this.operation.panoRotate) {
      this.operation.panoRotate = false
      this.dlon += -this.totalX * cam.fov / 930
      this.dlat += this.totalY * cam.fov / 930
    }
    if (this.operation.panoZooming) {
      this.operation.panoZooming = false
      // 相机的视觉随着鼠标滚动的距离拉进或者远离
      this.dzoom = this.totalWheelY * 0.03
      /* 角度缩放最值 */
      // cam.fov = THREE.MathUtils.clamp(fov, 45, 65)
      // cam.camera.updateProjectionMatrix()
    }
    // godMode
    if (this.operation.godRotate) {
      this.operation.godRotate = false
      this.dlon += this.totalX * 0.2
      if (cam.cameraType) {
        this.dlat += this.totalY * 0.2
      }
    }
    if (this.operation.godZooming) {
      this.operation.godZooming = false
      if (cam.cameraType) {
        this.dzoom += this.totalY * 200.0 + this.totalWheelY * 16
      } else {
        this.dzoom += -0.006 * this.totalY + -0.0005 * this.totalWheelY
        // cam.camera.zoom = cam.camera.zoom > 0.5 ? (cam.camera.zoom < 2.0 ? cam.camera.zoom : 2.0) : 0.5
        // cam.camera.updateProjectionMatrix()
      }
    }
    if (this.operation.godPanning) {
      this.operation.godPanning = false
      if (cam.cameraType) {
        this.dtranslate.x = this.totalX
        this.dtranslate.y = this.totalY
        // cam.panning(this.totalX, this.totalY)
      } else {
        this.dtranslate.x = this.totalX / 28
        this.dtranslate.y = this.totalY / 28
        // cam.panning(this.totalX / 28, this.totalY / 28)
      }
    }

    // 修正为非点击事件
    this.activate = this.dragFlag || (!this.rightUp && this.rightDown) || (!this.leftUp && this.leftDown) || this.Wheel
  }

  /**
   * 清除一帧内累计值
   */
  clearTransformation () {
    super.clearTransformation()
    this.rightDown = !this.rightUp && this.rightDown
    this.leftDown = !this.leftUp && this.leftDown

    this.dragFlag = (this.rightDown || this.leftDown) && this.dragFlag

    this.rightUp = false
    this.leftUp = false

    this.totalX = 0
    this.totalY = 0
    this.totalWheelY = 0
  }

  /**
   * 更新热点信息
   * @param {Hotpoint} interactHp
   */
  hotpointUpdate (interactHp) {
    if (this.currHotpoint !== interactHp) {
      if (!!this.currHotpoint && !!this.currHotpoint.onMouseOut) {
        this.currHotpoint.onMouseOut()
      }
      if (!!interactHp && !!interactHp.onMouseIn) {
        interactHp.onMouseIn()
      }
      this.currHotpoint = interactHp
    }
  }

  /**
   * 点位编辑UI更新
   * @param {} interactUi 编辑UI
   */
  cpointUiUpdate (interactUi) {
    if (this.currEditPoint !== interactUi) {
      this.currEditPoint = interactUi
    }
  }

  /**
   * 状态更新
   * @param {Hotpoint} interactHp
   * @param {*} interactUi
   */
  stateUpdate (interactHp, interactUi) {
    // 检测到hotpoint
    if (interactHp) {
      this.state = this.enumState.hotpoint
    } else if (interactUi) { // 检测到editpoint
      this.state = this.enumState.ui
    } else {
      this.state = this.enumState.free
    }
  }

  /**
   * 地面UI贴图更新
   * @param {THREE.Vector3} position
   */
  cpointUpdate (position) {
    // 贴图透明度变更
    this.cps.getCameraPoint(this.cam.cameraDestinationIndex).highLight = false
    // 更新点
    const rls = ScreenInput.findNearest(position, this.cps)

    // cam.findPoint(position)
    // 光标位置足够靠近点位才变色
    if (rls.dist < config.Cursor.InteractMaxDist * config.Cursor.InteractMaxDist) {
      this.cam.cameraDestinationIndex = rls.cpoint.id
      this.cps.getCameraPoint(this.cam.cameraDestinationIndex).highLight = true
    }
  }

  /**
   * 更新光标UI位置与方位
   */
  cursorUpdate () {
    // 更新光标是否可视
    if (this.state !== this.enumState.free) {
      this.visible = false
    } else {
      this.visible = true
      this.intersectRls.length = 0
      this.raycaster.intersectObjects(this.model.objects, false, this.intersectRls)
      const intersects = this.intersectRls
      // 如果射线碰到物体了
      if (intersects.length > 0) {
        const intersect = intersects[0]
        this.cpointUpdate(intersect.point)
        this.tapPosition.copy(intersect.point)

        // 如果按下则不刷新鼠标位置
        if (!this.rightDown) {
          // 根据鼠标悬停面法向改变光标形态
          if (!this.cursorMesh.isSprite) {
            // 移动光标形状物体到对应点上
            this.cursorMesh.position.copy(intersect.point).add(intersect.face.normal)
            this.cursorMesh.lookAt(intersect.face.normal.clone().add(this.cursorMesh.position))
          } else {
            // 移动光标形状物体到对应点上
            this.cursorMesh.position.copy(intersect.point)
            this.cursorMesh.position.add(intersect.face.normal.clone().multiplyScalar(this.isOffset ? 200 : 20))
          }
        }
      } else {
        this.visible = false
        this.state = this.enumState.None
      }
    }
  }

  /**
   * 处理Mouse触发的各类事件
   */
  handleMouseEvent () {
    // 左键点击
    if (this.leftUp) {
      if (!this.dragFlag) {
        if (this.state === this.enumState.free) {
          super.moveTo()
        } else {
          // todo 处理点击到热点的事件
          if (this.state === this.enumState.hotpoint) { // 处理热点交互
            if (this.currHotpoint.onClick) {
              this.currHotpoint.onClick()
            }
          }
          if (this.state === this.enumState.ui) { // 处理可编辑点位交互 // todo
            if (this.currEditPoint) {
              this.editpointSet.onClick(this.currEditPoint.object)
            }
          }
        }
      }
    }

    // 右键点击
    if (this.rightUp && !this.dragFlag) {
      if (this.state === this.enumState.free && this.intersectRls.length > 0 && !!this.rightClickCallback) {
        const dir = this.intersectRls[0].face.normal.clone()
        const pos = this.intersectRls[0].point.clone()
        const camPos = this.cam.camera.position
        const camIdx = this.cam.cameraCurrentIndex
        const fid = Utils.getFace(this.cps.getCameraPoint(camIdx).correctMat, pos.clone().sub(camPos))
        this.rightClickCallback(new CursorEvent(pos, dir, camPos, camIdx, fid))
      }
    }

    // 滚轮事件
    if (this.Wheel) {
      this.Wheel = false
      this.controller.toGod()
    }
  }

  /**
   * 检测光标是否可用
   * @param {Event} event 光标事件
   */
  isCouserUsable (event) {
    return this.inCanvas
  }

  /**
   * 设置光标图标，当tagUrl为空时，设置为默认图标
   * @param {string} tagUrl 光标图标的Url
   * @param {boolean} offset 是否需要沿着法线偏移防止遮挡
   */
  setIconTag (tagUrl, offset = true) {
    // 不为空时
    if (!!tagUrl && tagUrl !== '') {
      // 重新加载贴图
      new THREE.TextureLoader().loadAsync(tagUrl)
        .then(texture => {
          this.spriteIcon.material.map = texture
          // 设置图标
          this.spriteIcon.visible = this.cursorMesh === this.spriteIcon ? this.spriteIcon.visible : this.meshIcon.visible
          this.meshIcon.visible = false
          this.spriteIcon.position.copy(this.cursorMesh.position)
          this.cursorMesh = this.spriteIcon
        }, reason => {
          console.error(reason)
        })
    } else if (this.cursorMesh === this.spriteIcon) {
      // 重设图标
      this.meshIcon.visible = this.spriteIcon.visible
      this.spriteIcon.visible = false
      this.meshIcon.position.copy(this.cursorMesh.position)
      this.cursorMesh = this.meshIcon
    }
    this.isOffset = !tagUrl || offset
  }

  // 设置右键监听事件
  listenRightClick (callback) {
    this.rightClickCallback = callback
  }

  /**
   * Cursor更新方法
   */
  update () {
    if (this.inCanvas && this.enable) {
      this.interactUpdate()
    }
    this.handleMouseEvent()
  }
}

export {
  Cursor
}
